<div class="flex flex-col gap-6">
  <!-- DPE Status -->
  @if (property().dpe.state !== DpeState.DONE) {
    <div>
      <div
        class="flex items-center gap-3 p-4 rounded-2xl border"
        [class.bg-indigo-a2]="property().dpe.state === DpeState.DONE"
        [class.border-indigo-a4]="property().dpe.state === DpeState.DONE"
        [class.text-indigo-11]="property().dpe.state === DpeState.DONE"
        [class.bg-amber-a2]="
          property().dpe.state !== DpeState.DONE && property().dpe.state !== DpeState.NOT_SUBJECT
        "
        [class.border-amber-a4]="
          property().dpe.state !== DpeState.DONE && property().dpe.state !== DpeState.NOT_SUBJECT
        "
        [class.text-amber-11]="
          property().dpe.state !== DpeState.DONE && property().dpe.state !== DpeState.NOT_SUBJECT
        "
        [class.bg-gray-1]="property().dpe.state === DpeState.NOT_SUBJECT"
        [class.border-gray-6]="property().dpe.state === DpeState.NOT_SUBJECT"
        [class.text-gray-11]="property().dpe.state === DpeState.NOT_SUBJECT"
      >
        <mat-icon class="text-2xl">{{
          property().dpe.state === DpeState.DONE ? 'verified_user' : 'info'
        }}</mat-icon>
        <div class="flex flex-col">
          <span class="text-[10px] uppercase font-bold tracking-widest opacity-70"
            >Statut du diagnostic (DPE)</span
          >
          <span class="text-base font-bold">{{ dpeStatusLabel }}</span>
        </div>
      </div>
    </div>
  }

  @if (property().dpe.state === DpeState.DONE) {
    <div class="grid grid-cols-1 gap-8 p-4">
      <!-- DPE Energy Class -->
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-2 text-gray-11">
          <mat-icon class="text-sm">bolt</mat-icon>
          <span class="text-sm font-medium uppercase tracking-wider">Consommation Énergétique</span>
        </div>
        <etxe-energy-class-selector
          type="energy"
          [value]="property().dpe.energyClass"
          [readonly]="true"
        ></etxe-energy-class-selector>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <span class="text-xs font-semibold px-2 py-1 rounded bg-indigo-a2 text-indigo-9 tracking-tighter"
              >{{ property().dpe.energyConsumption || '-' }} kWh/m²/an (Primaire)</span
            >
            @if (property().dpe.finalEnergyConsumption) {
              <span
                class="text-xs font-semibold px-2 py-1 rounded bg-gray-2 text-gray-11 border border-gray-6 tracking-tighter"
                >{{ property().dpe.finalEnergyConsumption }} kWh/m²/an (Finale)</span
              >
            }
          </div>
        </div>
      </div>

      <!-- GHG Class -->
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-2 text-gray-11">
          <mat-icon class="text-sm">cloud_queue</mat-icon>
          <span class="text-sm font-medium uppercase tracking-wider">Émissions de CO2</span>
        </div>
        <etxe-energy-class-selector
          [type]="'ghg'"
          [value]="property().dpe.climateClass"
          [readonly]="true"
        ></etxe-energy-class-selector>
        <div class="flex flex-col gap-1">
          <span class="text-xs font-semibold px-2 py-1 rounded bg-indigo-a2 text-indigo-9 tracking-tighter"
            >{{ property().dpe.greenhouseGasEmission || '-' }} kg CO2/m²/an</span
          >
        </div>
      </div>
    </div>

    @if (
      property().dpe.estimatedCostMin || property().dpe.estimatedCostMax || property().dpe.priceIndexingDate
    ) {
      <div class="px-4">
        <div class="flex flex-col gap-4 rounded-xl border border-gray-a4 bg-gray-1 p-6">
          <div class="flex items-center gap-2 text-gray-11">
            <mat-icon class="text-sm">payments</mat-icon>
            <span class="text-xs font-bold uppercase tracking-wider">Estimation des coûts d'énergie</span>
          </div>

          <div class="flex items-end justify-between">
            <div class="flex flex-col">
              @if (property().dpe.estimatedCostMin || property().dpe.estimatedCostMax) {
                <span class="text-2xl font-bold text-indigo-11">
                  {{ property().dpe.estimatedCostMin || '-' }} -
                  {{ property().dpe.estimatedCostMax || '-' }} €/an
                </span>
              } @else {
                <span class="text-2xl font-bold text-gray-10">-</span>
              }
              @if (property().dpe.priceIndexingDate) {
                <span class="text-xs text-gray-10">
                  Basé sur les tarifs au {{ property().dpe.priceIndexingDate | date: 'dd/MM/yyyy' }}
                </span>
              }
            </div>
            <mat-icon class="text-gray-a5" fontIcon="trending_up"></mat-icon>
          </div>
        </div>
      </div>
    }

    <!-- DPE Metadata -->
    @if (property().dpe.ademeNumber || property().dpe.realizationDate) {
      <div class="p-4 rounded-xl bg-gray-2 border border-gray-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="flex flex-col">
          <span class="text-xs text-gray-10 uppercase tracking-tighter">N° de référence DPE</span>
          <span class="font-semibold text-gray-12">{{ property().dpe.ademeNumber || '-' }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-xs text-gray-10 uppercase tracking-tighter">Date de réalisation</span>
          <span class="font-semibold text-gray-12">{{
            (property().dpe.realizationDate | date: 'dd/MM/yyyy') || '-'
          }}</span>
        </div>
      </div>
    }

    <!-- Technical & Insulation Characteristics (ADEME) -->
    @if (property().dpe.technical; as tech) {
      <div class="p-4 rounded-2xl border border-neutral-3 bg-neutral-1 flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <mat-icon class="text-primary-10" fontIcon="verified"></mat-icon>
            <span class="text-sm font-bold text-neutral-12"
              >Caractéristiques thermiques & isolation (ADEME)</span
            >
          </div>
          <span
            class="inline-flex items-center gap-1 rounded-full border border-jade-a5 bg-jade-a2 px-2.5 py-0.5 text-xs font-semibold text-jade-11"
          >
            <mat-icon class="!h-3.5 !w-3.5 !text-xs leading-none">auto_awesome</mat-icon>
            Certifié ADEME
          </span>
        </div>

        <!-- Insulation Ratings Grid -->
        <div class="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
            <span class="text-xs text-neutral-10">Isolation des murs</span>
            <span
              [class]="
                'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                getQualityBadgeClass(tech.wallInsulation)
              "
            >
              <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                getQualityIcon(tech.wallInsulation)
              }}</mat-icon>
              {{ tech.wallInsulation || 'Non précisé' }}
            </span>
          </div>

          <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
            <span class="text-xs text-neutral-10">Menuiseries & vitrage</span>
            <span
              [class]="
                'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                getQualityBadgeClass(tech.glazingQuality)
              "
            >
              <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                getQualityIcon(tech.glazingQuality)
              }}</mat-icon>
              {{ tech.glazingQuality || 'Non précisé' }}
            </span>
          </div>

          <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
            <span class="text-xs text-neutral-10">Toiture & combles</span>
            <span
              [class]="
                'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                getQualityBadgeClass(tech.roofInsulation)
              "
            >
              <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                getQualityIcon(tech.roofInsulation)
              }}</mat-icon>
              {{ tech.roofInsulation || 'Non précisé' }}
            </span>
          </div>

          <div class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
            <span class="text-xs text-neutral-10">Plancher bas</span>
            <span
              [class]="
                'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                getQualityBadgeClass(tech.floorInsulation)
              "
            >
              <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                getQualityIcon(tech.floorInsulation)
              }}</mat-icon>
              {{ tech.floorInsulation || 'Non précisé' }}
            </span>
          </div>

          <div
            class="flex flex-col gap-1 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3 sm:col-span-2 lg:col-span-1"
          >
            <span class="text-xs text-neutral-10">Enveloppe globale</span>
            <span
              [class]="
                'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ' +
                getQualityBadgeClass(tech.envelopeQuality)
              "
            >
              <mat-icon class="!h-3 !w-3 !text-xs leading-none">{{
                getQualityIcon(tech.envelopeQuality)
              }}</mat-icon>
              {{ tech.envelopeQuality || 'Non précisé' }}
            </span>
          </div>
        </div>

        <!-- Technical equipment details -->
        <div class="grid grid-cols-1 gap-2.5 border-t border-neutral-3 pt-3 sm:grid-cols-2 lg:grid-cols-4">
          <div class="flex flex-col gap-0.5 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
            <span class="text-xs text-neutral-10">Hauteur sous plafond</span>
            <span class="text-xs font-semibold text-neutral-12">
              {{ tech.ceilingHeight ? tech.ceilingHeight + ' m' : 'Non renseignée' }}
            </span>
          </div>

          <div class="flex flex-col gap-0.5 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
            <span class="text-xs text-neutral-10">Chauffage principal</span>
            <span
              class="text-xs font-semibold text-neutral-12 truncate"
              [title]="
                (tech.heatingType ? (tech.heatingType | titlecase) : '') +
                (tech.heatingEnergy ? ' (' + tech.heatingEnergy + ')' : '')
              "
            >
              {{ tech.heatingType ? (tech.heatingType | titlecase) : '' }}
              {{
                tech.heatingEnergy ? '(' + tech.heatingEnergy + ')' : !tech.heatingType ? 'Non précisé' : ''
              }}
            </span>
          </div>

          <div class="flex flex-col gap-0.5 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
            <span class="text-xs text-neutral-10">Climatisation</span>
            <div class="flex items-center gap-1">
              @if (tech.hasAirConditioning) {
                <span
                  class="inline-flex items-center gap-1 rounded-md border border-jade-a5 bg-jade-a2 px-2 py-0.5 text-xs font-semibold text-jade-11"
                >
                  <mat-icon class="!h-3 !w-3 !text-xs leading-none">ac_unit</mat-icon>
                  Installée
                </span>
              } @else {
                <span class="text-xs text-neutral-10">Non détectée</span>
              }
            </div>
          </div>

          <div class="flex flex-col gap-0.5 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3">
            <span class="text-xs text-neutral-10">Ventilation</span>
            <span
              class="text-xs font-semibold text-neutral-12 truncate"
              [title]="tech.ventilationType || 'Non précisée'"
            >
              {{ tech.ventilationType || 'Non précisée' }}
            </span>
          </div>

          @if (tech.heatingGenerator) {
            <div
              class="flex flex-col gap-0.5 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3 sm:col-span-2"
            >
              <span class="text-xs text-neutral-10">Générateur / système de production</span>
              <span class="text-xs font-semibold text-neutral-12">{{ tech.heatingGenerator }}</span>
            </div>
          }

          @if (tech.heatingEmitter) {
            <div
              class="flex flex-col gap-0.5 rounded-xl border border-neutral-a3 bg-neutral-2/50 p-3 sm:col-span-2"
            >
              <span class="text-xs text-neutral-10">Émetteur(s) de chaleur</span>
              <span class="text-xs font-semibold text-neutral-12">{{ tech.heatingEmitter }}</span>
            </div>
          }
        </div>
      </div>
    }
  }
</div>
